/**
 * Model-wise Reports pages.
 *
 * `slug` is the URL segment (`/reports/<slug>`), `key` is the model identifier the
 * command-center's per-model event tables use — they differ because the API keys
 * are snake_case and URLs read better kebab-cased. Kept in the shared package so
 * the local and cloud report routes cannot drift apart.
 */
export interface ReportModel {
  slug: string;
  key: string;
  label: string;
  /**
   * One line on how this model reaches its verdict, shown in the footer of a single event.
   * Only where the answer isn't self-evident from the facts: a reader looking at "Aarti
   * Kulkarni, 94%" is entitled to know what that number was measured against. Left unset for
   * models whose detection needs no such caveat.
   */
  note?: string;
}

export const REPORT_MODELS: ReportModel[] = [
  { slug: 'face', key: 'face', label: 'FRS Face Recognition', note: 'Matched against the registered face vector set' },
  { slug: 'person', key: 'person', label: 'Person Detection' },
  { slug: 'person-count', key: 'person_count', label: 'Person Count' },
  { slug: 'intrusion', key: 'intrusion', label: 'Intrusion Detection' },
  // The one row whose label is deliberately NOT its key title-cased. The product calls this
  // Vehicle Detection; the key, the slug, the `vehicle_crossing_events` table and every row
  // already stored still say "crossing", and renaming any of those would be renaming data. If
  // you are here because the label and the slug disagree — that is on purpose.
  { slug: 'vehicle-crossing', key: 'vehicle_crossing', label: 'Vehicle Detection' },
  { slug: 'ppe-violation', key: 'ppe_violation', label: 'PPE Violation' },
  { slug: 'theft', key: 'theft', label: 'Theft Detection' },
  { slug: 'fall', key: 'fall', label: 'Fall Detection' },
  {
    slug: 'fire-smoke',
    key: 'fire_smoke',
    label: 'Fire & Smoke Detection',
    note: 'Fire and smoke come from one detector; the Type column says which class each detection was',
  },
  {
    slug: 'ocr',
    key: 'ocr',
    label: 'Text & Plate Reads (OCR)',
    note: 'Every plate and line of text the OCR model read in a frame. To find a word or plate with spelling mistakes allowed, use Search → All',
  },
];

/** Default report when a slug is missing or unrecognised. */
export const DEFAULT_REPORT_SLUG = 'person';

export function reportModelBySlug(slug?: string): ReportModel | undefined {
  return REPORT_MODELS.find((m) => m.slug === slug);
}

export function reportModelLabel(key?: string): string {
  return REPORT_MODELS.find((m) => m.key === key)?.label || 'AI Events';
}

export function reportModelByKey(key?: string): ReportModel | undefined {
  return REPORT_MODELS.find((m) => m.key === key);
}

/** The model's footer caveat, if it has one. */
export function reportModelNote(key?: string): string | undefined {
  return REPORT_MODELS.find((m) => m.key === key)?.note;
}

/**
 * Model key → URL segment, for links that deep-link a single event
 * (`/events/<slug>/<id>`). Falls back to the key itself so a model that has no report page
 * still produces a working link rather than a dead one.
 */
export function reportModelSlug(key?: string): string {
  return REPORT_MODELS.find((m) => m.key === key)?.slug || key || DEFAULT_REPORT_SLUG;
}
